// Thin adapter over the shared message catalogs (frontend/lang/<locale>.json).
//
// Historically this file held hand-maintained EN/ES/FR tables for the survey
// take/results screens. Those strings now live in the catalogs under the
// `results.*` namespace (single source of truth, agent-translatable). This
// adapter preserves the old `getUIStrings(lang)` call sites while reading from
// the catalog, so the result screens stay in lockstep with every other surface.

import { catalogFor } from './catalog'
import { SOURCE_LOCALE } from './config'

export type UIStrings = {
  assessmentComplete: string
  surveyComplete: string
  thankYou: string
  resultsForTitle: (title: string) => string
  scoredOnTitle: (title: string) => string
  backToHome: string
  overallScore: string
  overall: string
  points: string
  pts: string
  questionCount: (count: number) => string
  excellent: string
  good: string
  fair: string
  needsFocus: string
  strong: string
  moderate: string
  needsAttention: string
  responseRecorded: string
}

const NS = 'results.'

export function getUIStrings(lang: string | undefined): UIStrings {
  const catalog = catalogFor(lang || SOURCE_LOCALE)
  const source = catalogFor(SOURCE_LOCALE)
  // Survey-take results legitimately fall back to the source string when a
  // respondent's survey language has no UI catalog yet (content-translation
  // path). This is distinct from admin chrome, which must never fall back.
  const g = (key: string): string => catalog[NS + key] ?? source[NS + key] ?? ''
  const interpolate = (key: string, title: string): string =>
    g(key).replace('{title}', title)

  return {
    assessmentComplete: g('assessmentComplete'),
    surveyComplete: g('surveyComplete'),
    thankYou: g('thankYou'),
    resultsForTitle: (t) => interpolate('resultsForTitle', t),
    scoredOnTitle: (t) => interpolate('scoredOnTitle', t),
    backToHome: g('backToHome'),
    overallScore: g('overallScore'),
    overall: g('overall'),
    points: g('points'),
    pts: g('pts'),
    questionCount: (count) =>
      g(count === 1 ? 'questionCountOne' : 'questionCountMany').replace('{count}', String(count)),
    excellent: g('excellent'),
    good: g('good'),
    fair: g('fair'),
    needsFocus: g('needsFocus'),
    strong: g('strong'),
    moderate: g('moderate'),
    needsAttention: g('needsAttention'),
    responseRecorded: g('responseRecorded'),
  }
}
